import React, { useState, useEffect } from 'react';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus } from '../../utils/campLogic';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [filterMode, setFilterMode] = useState('evergreen');

  const fetchUsers = async () => {
    setLoading(true);
    const [usersRes, settingsRes] = await Promise.all([
      api.get('users_gwacamp'),
      api.get('settings_gwacamp')
    ]);
    setUsers(Array.isArray(usersRes) ? usersRes : usersRes.data || []);
    setSettings(Array.isArray(settingsRes) ? settingsRes[0] : settingsRes?.data?.[0] || {});
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const toggleAllAccess = async (user) => {
    let newPass = user.pass_type === 'allaccess' ? 'free' : 'allaccess';
    
    // Handle evergreen types
    if (user.pass_type === 'evergreen') newPass = 'evergreen-aap';
    else if (user.pass_type === 'evergreen-aap') newPass = 'evergreen';

    await api.put('users_gwacamp', user.id, { pass_type: newPass });
    fetchUsers();
  };

  const toggleEarlyAccess = async (user) => {
    const newEarlyAccess = user.early_access == 1 ? 0 : 1;
    await api.put('users_gwacamp', user.id, { early_access: newEarlyAccess });
    fetchUsers();
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Are you sure you want to completely remove ${user.name}? This action cannot be undone.`)) return;
    
    setDeletingId(user.id);
    try {
      await api.delete('users_gwacamp', user.id);
      fetchUsers();
    } catch (err) {
      console.error("Failed to delete user", err);
      alert("Failed to remove user. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || 
                          u.email?.toLowerCase().includes(search.toLowerCase());
    
    if (filterMode === 'evergreen') {
      return matchesSearch && ['evergreen', 'evergreen-aap'].includes(u.pass_type);
    }
    return matchesSearch;
  });

  const getPassBadge = (type) => {
    switch (type) {
      case 'allaccess':
      case 'evergreen-aap':
        return 'bg-amber/20 text-amber';
      case 'evergreen':
        return 'bg-forest-green/20 text-forest-green';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  // Funnel calculations for Evergreen Cohort
  const evergreenUsers = users.filter(u => ['evergreen', 'evergreen-aap'].includes(u.pass_type));
  const now = new Date();
  const oneWeekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);

  const regsThisWeek = evergreenUsers.filter(u => {
    if (!u.trail_start_date) return false;
    const [y, m, d] = u.trail_start_date.split('-');
    const start = new Date(y, m - 1, d);
    return start >= oneWeekAgo;
  }).length;

  const dayDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 'Completed': 0 };
  evergreenUsers.forEach(u => {
    const { currentTrailDay } = getCampStatus(u, settings);
    if (currentTrailDay >= 1 && currentTrailDay <= 5) {
      dayDistribution[currentTrailDay]++;
    } else if (currentTrailDay > 5) {
      dayDistribution['Completed']++;
    }
  });

  const day5Completions = dayDistribution['Completed'];
  const aapConversions = evergreenUsers.filter(u => u.pass_type === 'evergreen-aap').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <div className="flex bg-gray-100 p-1 rounded-md">
            <button
              onClick={() => setFilterMode('evergreen')}
              className={`px-3 py-1 text-sm font-bold rounded transition-colors ${filterMode === 'evergreen' ? 'bg-white shadow text-forest-green' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Evergreen Cohort
            </button>
            <button
              onClick={() => setFilterMode('all')}
              className={`px-3 py-1 text-sm font-bold rounded transition-colors ${filterMode === 'all' ? 'bg-white shadow text-dark-green' : 'text-gray-500 hover:text-gray-700'}`}
            >
              All Campers
            </button>
          </div>
        </div>
        <div className="w-64">
          <Input 
            placeholder="Search users..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>
      </div>

      {filterMode === 'evergreen' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-forest-green text-white">
            <div className="text-sm opacity-80 uppercase tracking-wider font-bold">New This Week</div>
            <div className="text-3xl font-bold mt-1">{regsThisWeek}</div>
          </Card>
          
          <Card className="p-4 border-forest-green/20">
            <div className="text-sm text-gray-500 uppercase tracking-wider font-bold">On Trail (D1-D5)</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">
              {dayDistribution[1] + dayDistribution[2] + dayDistribution[3] + dayDistribution[4] + dayDistribution[5]}
            </div>
            <div className="flex gap-2 text-xs text-gray-500 mt-2 font-mono">
              <span>D1: {dayDistribution[1]}</span>
              <span>D2: {dayDistribution[2]}</span>
              <span>D3: {dayDistribution[3]}</span>
              <span>D4: {dayDistribution[4]}</span>
              <span>D5: {dayDistribution[5]}</span>
            </div>
          </Card>

          <Card className="p-4 border-forest-green/20">
            <div className="text-sm text-gray-500 uppercase tracking-wider font-bold">Completions</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">{day5Completions}</div>
            <div className="text-xs text-gray-400 mt-1">Past Day 5</div>
          </Card>

          <Card className="p-4 bg-amber/10 border-amber/20">
            <div className="text-sm text-amber-700 uppercase tracking-wider font-bold">AAP Conversions</div>
            <div className="text-3xl font-bold text-amber-600 mt-1">{aapConversions}</div>
            <div className="text-xs text-amber-600/70 mt-1 font-medium">
              {evergreenUsers.length > 0 ? Math.round((aapConversions / evergreenUsers.length) * 100) : 0}% of cohort
            </div>
          </Card>
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Pass Type</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Genre / Stage</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400">Loading campers...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400">No campers found</td></tr>
              ) : filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-bold text-gray-900">{user.name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 rounded-full capitalize">{user.role}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase ${getPassBadge(user.pass_type)}`}>
                      {user.pass_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {user.trail_start_date || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {user.genre || '-'} / {user.journey_stage?.replace('_', ' ') || '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end items-center gap-2">
                      <Button
                        variant="outline"
                        className={`text-xs py-1 px-2 border ${user.early_access == 1 ? 'border-amber text-amber hover:bg-amber hover:text-white' : 'border-forest-green text-forest-green hover:bg-forest-green hover:text-white'}`}
                        onClick={() => toggleEarlyAccess(user)}
                        title={user.early_access == 1 ? 'Revoke Early Access' : 'Grant Early Access'}
                      >
                        <SafeIcon name={user.early_access == 1 ? "Lock" : "Unlock"} className="text-sm" />
                      </Button>
                      <Button 
                        variant={['allaccess', 'evergreen-aap'].includes(user.pass_type) ? 'outline' : 'secondary'} 
                        className="text-xs py-1"
                        onClick={() => toggleAllAccess(user)}
                      >
                        {['allaccess', 'evergreen-aap'].includes(user.pass_type) ? 'Downgrade' : 'Upgrade'}
                      </Button>
                      <Button
                        variant="outline"
                        className="text-xs py-1 px-2 border-red-200 text-red-600 hover:text-white hover:bg-red-600 hover:border-red-600"
                        onClick={() => removeUser(user)}
                        disabled={deletingId === user.id}
                        title="Remove Camper"
                      >
                        {deletingId === user.id ? (
                          <SafeIcon name="Loader" className="animate-spin text-sm" />
                        ) : (
                          <SafeIcon name="Trash2" className="text-sm" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}